// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $ChampionshipAdminRequestResource,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
} from '../../../../helpers';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$ChampionshipAdminRequestResource>;
  loading: boolean;
};

class ChampionshipAdminProposesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionSeasonId === data.RegionSeasonId
          && data.modelName === 'ChampionshipAdminRequest'
        ) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getResourceChampionshipAdminRequest({
            RegionSeasonId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderDataItem(dataItem: $ChampionshipAdminRequestResource) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <tr key={dataItem.id}>
        <TdDate
          duration
          multiple
          value={dataItem.createdAt}
        />
        <td>{dataItem.Championship.Name}</td>
        <td>{dataItem.Name}</td>
        <td>{dataItem.Email}</td>
        <td>
          <div className={'d-flex justify-content-end'}>
            <DeleteDialog
              content={t('Do you really want to revoke?')}
              id={`delete-championship-admin-request-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelDeleteButton={t('Delete')}
              onAction={() => defaultDeleteItem({
                action: async () => Services.Api.deleteChampionshipAdminRequest(dataItem),
              })}
              subTitle={dataItem.Name || ''}
              title={t('Championship admin request')}
            >
              <Button
                color={'danger'}
                id={`delete-championship-admin-request-button-${id}`}
                outline
                size={'sm'}
              >
                {t('Revoke')}
              </Button>
            </DeleteDialog>
          </div>
        </td>
      </tr>
    );
  }

  renderData() {
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $ChampionshipAdminRequestResource) => this.renderDataItem(dataItem))}
      </>
    );
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <SectionHeader
        bottomBorder
        title={t('Pending championship admin requests')}
      />
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Date')}</th>
            <th>{t('Championship')}</th>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {this.renderData()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
    } = this.state;

    return (data.length > 0 ? (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    ) : null);
  }
}

export default ChampionshipAdminProposesSectionView;
